/* global BigInt */
import { parseUnits, Interface, ethers } from 'ethers';
import { saveTransactionHashAtDb } from '../apis/validator/validating';
import { toast } from 'sonner';
import * as web3 from '@solana/web3.js';
import * as splToken from '@solana/spl-token';
import { BATCH_MAX, MULTISEND_CONTRACTADDRESS } from '../constants/apiPaths';
import { getRpcUrl } from '../apis/geenpills/getRPCURL';
// ABI for the mint function
const contractABI = [
    "function multiSend(address token, address[] calldata recipients, uint256[] calldata amounts) external"
];
export const fetchChainDetails = async (chainId) => {
    try {
        // Fetch the list of all chains
        const response = await fetch('https://chainid.network/chains.json');
        const chains = await response.json();

        // Find the chain matching the targetChainId
        const chainDetails = chains.find((chain) => chain.chainId === Number(chainId));

        if (!chainDetails) {
            throw new Error(`Chain with ID ${chainId} not found.`);
        }

        // Extract necessary details
        return {
            chainId: `0x${Number(chainDetails.chainId).toString(16)}`, // Convert to hex
            rpcUrls: chainDetails.rpc, // Array of RPC URLs
            chainName: chainDetails.name,
            nativeCurrency: {
                name: chainDetails.nativeCurrency.name,
                symbol: chainDetails.nativeCurrency.symbol,
                decimals: chainDetails.nativeCurrency.decimals || 18,
            },
            blockExplorerUrls: chainDetails.explorers?.map((explorer) => explorer.url) || [],
        };
    } catch (error) {
        console.error('Error fetching chain details:', error);
        throw new Error('Failed to fetch chain details.');
    }
};

export const getMetaMaskProvider = () => {
    if (window.ethereum && window.ethereum.providers) {
        // Multiple providers detected
        const providers = window.ethereum.providers;

        // Find MetaMask's provider
        const metaMaskProvider = providers.find((provider) => provider.isMetaMask);

        return metaMaskProvider;
    } else if (window.ethereum && window.ethereum.isMetaMask) {
        // Single provider detected, and it's MetaMask
        return window.ethereum;
    } else {
        // MetaMask is not installed
        return null;
    }
};

export const getERC20Balance = async (tokenAddress, walletAddress, chainId) => {
    // Connect to the blockchain
    const provider = ethers.getDefaultProvider(Number(chainId), {
        alchemy: process.env.ALCHEMY_API_KEY,
        quorum: 1,
    });

    // const provider = getMetaMaskProvider();
    // ABI for ERC20 `balanceOf` and `decimals` functions
    const ERC20_ABI = [
        'function balanceOf(address _owner) view returns (uint256)',
        'function decimals() view returns (uint8)',
    ];

    // Create a contract instance
    const contract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);

    try {
        // Get the token balance

        const rawBalance = await contract.balanceOf(walletAddress);

        // Get the token decimals
        const decimals = await contract.decimals();

        // Convert balance to a human-readable format
        const balance = Number(rawBalance) / Math.pow(10, Number(decimals));

        return balance;
    } catch (error) {
        throw error;
    }
};

// Example usage
// (async () => {
//   const chainId = 11155420;
//   const tokenAddress = "0x7a515022eC0a175dB9600E6E11461e031eaF8c95"; // Replace with the ERC20 token address
//   const walletAddress = "0xf8663881CcC3396776BB15671ee997B020AE3706"; // Replace with the wallet address

//   try {
//     const balance = await getERC20Balance(tokenAddress, walletAddress, chainId);
//     console.log("Token Balance:", balance);
//   } catch (error) {
//     console.error("Error:", error);
//   }
// })();

export const finaliseGrading = async ({
    tokenAddress,
    rewardsDistribution,
    provider,
    decimals,
    sdk,
    wallet,
    platformFee,
    bountyId,
    chainId,
}) => {
    const metaMaskProvider = getMetaMaskProvider();
    const transactionHashes = [];
    const rewardsArray = [...rewardsDistribution];
    try {
        if (platformFee > 0) rewardsArray.push({ evmAddress: wallet, rewardAmount: platformFee });
        for (let i = 0; i < rewardsArray.length; i++) {
            const transferAmount = parseUnits(rewardsArray[i]?.rewardAmount?.toString(), decimals); // Adjust decimals if needed (e.g., 6 for USDC)

            const accounts = await metaMaskProvider.request({ method: 'eth_requestAccounts' });
            const from = accounts[0];
            const transferData = new Interface([
                'function transfer(address to, uint256 value) public returns (bool)',
            ]).encodeFunctionData('transfer', [rewardsArray[i]?.evmAddress, transferAmount]);

            const transactionParams = {
                to: tokenAddress, // ERC-20 contract address
                from: from, // Fetch connected account
                data: transferData, // Encoded function call
                value: '0x0', // No ETH transfer
            };

            // Send transaction
            const result = await metaMaskProvider.request({
                method: 'eth_sendTransaction',
                params: [transactionParams],
            });
            transactionHashes.push(result);
        }
    } catch (error) {
        console.error('Error during transfer:', error);
        return { success: false, code: error?.message };
    }

    try {
        const res = await saveTransactionHashAtDb({
            bountyId: bountyId,
            chainId: chainId,
            transactionHashes: transactionHashes,
        });
    } catch (error) {
        return error;
    }

    return { success: true };
};

export const fundBounty = async ({ tokenAddress, receiver, amount, decimals, chainId }) => {
    const metaMaskProvider = getMetaMaskProvider();

    if (!metaMaskProvider) {
        const installUrl = 'https://metamask.io/download.html';
        toast.info('Opening MetaMask installation page in a new tab...');
        window.open(installUrl, '_blank'); // Open the URL in a new tab
        // toast.error('MetaMask is not installed or unavailable');
        return;
    }

    // Request accounts using MetaMask provider
    const accounts = await metaMaskProvider.request({ method: 'eth_requestAccounts' });
    if (!accounts || accounts.length === 0) {
        toast.error('Failed to connect wallet');
        return;
    }

    // Detect chain ID
    const userChainId = await metaMaskProvider.request({ method: 'eth_chainId' });
    const targetChainId = `0x${parseInt(chainId, 10).toString(16)}`; // Convert to hex string

    if (userChainId !== targetChainId) {
        try {
            // Attempt to switch to the correct chain
            await metaMaskProvider.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: targetChainId }],
            });
            toast.success(`Switched to chain ID: ${targetChainId}`);
        } catch (switchError) {
            // Handle the case where the chain is not added or user rejects the switch

            if (switchError.code === 4902) {
                // 4902: Chain not added to MetaMask
                try {
                    const chainDetails = await fetchChainDetails(chainId);
                    await metaMaskProvider.request({
                        method: 'wallet_addEthereumChain',
                        params: [chainDetails],
                    });
                    toast.success(`Added and switched to chain ID: ${targetChainId}`);
                } catch (addError) {
                    toast.error('Failed to add or switch to the required chain');
                    return;
                }
            } else {
                toast.error('Please change your chain network manually');
                return;
            }
        }
    }

    try {
        const transferAmount = parseUnits(amount?.toString(), decimals); // Adjust decimals if needed (e.g., 6 for USDC)

        const accounts = await metaMaskProvider.request({ method: 'eth_requestAccounts' });
        const from = accounts[0];
        const transferData = new Interface([
            'function transfer(address to, uint256 value) public returns (bool)',
        ]).encodeFunctionData('transfer', [receiver, transferAmount]);

        const transactionParams = {
            to: tokenAddress, // ERC-20 contract address
            from: from, // Fetch connected account
            data: transferData, // Encoded function call
            value: '0x0', // No ETH transfer
        };

        // Send transaction
        const result = await metaMaskProvider.request({
            method: 'eth_sendTransaction',
            params: [transactionParams],
        });
        return { success: true, transactionHash: result };
        // result will be the txn hash used in the API call
    } catch (error) {
        console.error('Error during transfer:', error);
        return { success: false, code: error?.message };
    }
};

export const finaliseGradingSolana = async (
    tokenAddress,
    rewardsDistribution,
    fromWalletAddress,
    platformFee,
    decimals,
    bountyId,
    chainId,
    connection,
    phantom,
    platformWallet
) => {
    const transactionHashes = [];
    console.log(rewardsDistribution, 'now implement solana rewarding');
    const rewardsArray = [...rewardsDistribution];

    try {
        if (platformFee > 0) {
            rewardsArray.push({ walletAddress: platformWallet, rewardAmount: platformFee });
        }

        const senderAddress = new web3.PublicKey(fromWalletAddress);
        const mintAddress = new web3.PublicKey(tokenAddress);
        const senderTokenAccount = splToken.getAssociatedTokenAddressSync(mintAddress, senderAddress);

        for (let i = 0; i < rewardsArray.length; i++) {
            const instructions = [];
            const recipientAddress = new web3.PublicKey(rewardsArray[i].walletAddress);
            const recipientTokenAccount = splToken.getAssociatedTokenAddressSync(mintAddress, recipientAddress, true);

            const recipientInfo = await connection.getAccountInfo(recipientTokenAccount);
            if (!recipientInfo) {
                instructions.push(
                    splToken.createAssociatedTokenAccountInstruction(
                        senderAddress,
                        recipientTokenAccount,
                        recipientAddress,
                        mintAddress
                    )
                );
            }

            const amount = BigInt(Math.floor(Number(rewardsArray[i].rewardAmount) * 10 ** decimals));

            instructions.push(
                splToken.createTransferInstruction(senderTokenAccount, recipientTokenAccount, senderAddress, amount)
            );

            const blockhash = (await connection.getLatestBlockhash()).blockhash;

            const message = new web3.TransactionMessage({
                payerKey: senderAddress,
                recentBlockhash: blockhash,
                instructions,
            }).compileToV0Message();

            const transaction = new web3.VersionedTransaction(message);

            const signature = await phantom.adapter.sendTransaction(transaction, connection);

            const confirmResult = await connection.confirmTransaction(signature, 'confirmed');

            transactionHashes.push(signature);
        }
    } catch (error) {
        console.error('Error during Solana transfer:', error);
        return { success: false, code: error?.message };
    }

    try {
        await saveTransactionHashAtDb({
            bountyId,
            chainId,
            transactionHashes,
        });
    } catch (error) {
        return { success: false, code: error };
    }

    return { success: true };
};














async function transferRewards(receivers,amounts,contractAddress,chainId) {

    // const contractAddress = '0x2dCB0430b2CD45d30C3C8A3fDe0BBAbB2b11DE4D'
    // const chainId = 84532;
  
    try {
        const provider = new ethers.JsonRpcProvider(getRpcUrl(chainId));
        const wallet = new ethers.Wallet(process.env.TESTER2, provider);
        const contract = new ethers.Contract(contractAddress, contractABI, wallet);
            await contract.multiSend("0x7D360E10bf73e7171136c5348670ebdb3a67Ecd3", receivers, amounts)
            await new Promise(resolve => setTimeout(resolve, 500))
        //}
    } catch (error) {
        console.error('Error during minting:', error);
        throw error;
    }
}






async function approveTransfer(tokenAddress,decimals,amount) {
    const metaMaskProvider = getMetaMaskProvider();
    try {
            const transferAmount = parseUnits(amount.toString(), decimals)// Adjust decimals if needed (e.g., 6 for USDC)
            const accounts = await metaMaskProvider.request({ method: 'eth_requestAccounts' });
            const from = accounts[0];
            const approveData = new Interface([
            "function approve(address spender, uint256 amount) external returns (bool)",
            ]).encodeFunctionData('approve', [MULTISEND_CONTRACTADDRESS, transferAmount]);

            const transactionParams = {
                to: tokenAddress, // ERC-20 contract address
                from: from, // Fetch connected account
                data: approveData, // Encoded function call
                value: '0x0', // No ETH transfer
            };

            // Send transaction
            const result = await metaMaskProvider.request({
                method: 'eth_sendTransaction',
                params: [transactionParams],
            });
          
      
    } catch (error) {
        console.error('Error during transfer:', error);
        return { success: false, code: error?.message };
    }

  
    return { success: true };
};


export const finaliseGradingWithBatching = async ({
    tokenAddress,
    rewardsDistribution,
    decimals,
    wallet,
    platformFee,
    bountyId,
    chainId,
}) => {
    // get contract address from .env
    const contractAddress=MULTISEND_CONTRACTADDRESS
    const rewardsArray = [...rewardsDistribution];

    
    if (platformFee > 0) rewardsArray.push({ evmAddress: wallet, rewardAmount: platformFee });
    const totalAmountToTransfer = rewardsArray?.reduce((acc, item) => acc + item?.rewardAmount, 0);
    await approveTransfer(tokenAddress,decimals,totalAmountToTransfer)
    const metaMaskProvider = getMetaMaskProvider();
    const transactionHashes = [];


    const receivers = rewardsArray.map((item) => item?.evmAddress);
    const amounts = rewardsArray.map((item) => parseUnits(item?.rewardAmount?.toString(), decimals));
    const numberOfBatches = Math.ceil(receivers.length /BATCH_MAX );
    try {
        // if (platformFee > 0) rewardsArray.push({ evmAddress: wallet, rewardAmount: platformFee });
        for (let i = 0; i < numberOfBatches; i++) {
            const batchReceivers = receivers.slice(i*BATCH_MAX,(i+1)*BATCH_MAX)
            const batchAmounts = amounts.slice(i*BATCH_MAX,(i+1)*BATCH_MAX)
            const accounts = await metaMaskProvider.request({ method: 'eth_requestAccounts' });
            const from = accounts[0];
            const transferData = new Interface([
                "function multiSend(address token, address[] calldata recipients, uint256[] calldata amounts) external"
            ]).encodeFunctionData('multiSend', [tokenAddress,batchReceivers,batchAmounts]);

            const transactionParams = {
                to: contractAddress, // ERC-20 contract address
                from: from, // Fetch connected account  
                data: transferData, // Encoded function call
                value: '0x0', // No ETH transfer
            };

            // Send transaction
            const result = await metaMaskProvider.request({
                method: 'eth_sendTransaction',
                params: [transactionParams],
            });
            transactionHashes.push(result);
        }
    } catch (error) {
        console.error('Error during transfer:', error);
        return { success: false, code: error?.message };
    }

    try {
        const res = await saveTransactionHashAtDb({
            bountyId: bountyId,
            chainId: chainId,
            transactionHashes: transactionHashes,
        });
    } catch (error) {
        return error;
    }

    return { success: true };
};
